<template>
  <v-card tile flat style="height:100%;">
    <v-card-text>
      <div style="text-align:left; margin:0.5rem;margin-bottom:1.5rem">
        <div class="headline">Certificates</div>
        <div class="body-1">
          Manage device certificates for IoT authentication. Create self-signed,
          CSR, or AWS-managed certificates.
        </div>
      </div>
      <v-layout row wrap>
        <v-flex xs12 sm12 md12>
          <itemlist
            :headers="[
              {text: 'Certificate ID', value: 'certificate_id'},
              {text: 'Device ID', value: 'device_id'},
              {text: 'Type', value: 'cert_type'},
              {text: 'Algorithm', value: 'key_algorithm'},
              {text: 'Status', value: 'status'},
              {text: 'Created', value: 'created_at'},
            ]"
            :values="certificates"
            v-on:selected="certificate = $event"
            v-on:unselected="certificate = null"
          ></itemlist>
        </v-flex>
        <v-flex xs12 sm12 md12 v-if="certificate">
          <v-card class="elevation-0">
            <v-card-title class="title" style="padding-top:1rem">
              <span
                >Details of
                <v-chip>{{ certificate.certificate_id }}</v-chip>
                certificate</span
              >
            </v-card-title>
            <v-card-text style="padding-top:0;padding-bottom:0">
              <v-layout row wrap style="text-align: left;margin-left:2rem">
                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px"
                        >fingerprint</v-icon
                      >Certificate ID:</v-flex
                    >
                    <v-flex xs9>{{ certificate.certificate_id }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">devices</v-icon
                      >Device ID:</v-flex
                    >
                    <v-flex xs9>{{ certificate.device_id }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">label</v-icon
                      >Type:</v-flex
                    >
                    <v-flex xs9>
                      <v-chip
                        :color="getCertTypeColor(certificate.cert_type)"
                        text-color="white"
                        small
                      >
                        {{ certificate.cert_type }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">vpn_key</v-icon
                      >Algorithm:</v-flex
                    >
                    <v-flex xs9
                      >{{ certificate.key_algorithm
                      }}{{
                        certificate.key_size
                          ? ` (${certificate.key_size}-bit)`
                          : ""
                      }}</v-flex
                    >
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">flag</v-icon
                      >Status:</v-flex
                    >
                    <v-flex xs9>
                      <v-chip
                        :color="getStatusColor(certificate.status)"
                        text-color="white"
                        small
                      >
                        {{ certificate.status }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">date_range</v-icon
                      >Created:</v-flex
                    >
                    <v-flex xs9>{{
                      formatDate(certificate.created_at)
                    }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="certificate.expires_at">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">event</v-icon
                      >Expires:</v-flex
                    >
                    <v-flex xs9>{{
                      formatDate(certificate.expires_at)
                    }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="certificate.has_private_key">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">lock</v-icon
                      >Private Key:</v-flex
                    >
                    <v-flex xs9
                      ><v-chip color="green" text-color="white" small
                        >Available</v-chip
                      ></v-flex
                    >
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="certificate.has_certificate">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px"
                        >verified_user</v-icon
                      >Certificate:</v-flex
                    >
                    <v-flex xs9
                      ><v-chip color="blue" text-color="white" small
                        >Available</v-chip
                      ></v-flex
                    >
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="certificate.has_csr">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px"
                        >description</v-icon
                      >CSR:</v-flex
                    >
                    <v-flex xs9
                      ><v-chip color="purple" text-color="white" small
                        >Available</v-chip
                      ></v-flex
                    >
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="certificate.arn">
                  <v-layout>
                    <v-flex xs3
                      ><v-icon small style="margin-right:5px">cloud</v-icon>AWS
                      ARN:</v-flex
                    >
                    <v-flex xs9 style="word-break: break-all;">{{
                      certificate.arn
                    }}</v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-actions style="padding-top:0;">
              <v-spacer></v-spacer>
              <v-btn
                outline
                small
                color="blue"
                v-on:click="downloadCertificate(certificate.certificate_id)"
                :loading="downloadingCertId === certificate.certificate_id"
              >
                <v-icon small>download</v-icon> Download
              </v-btn>
              <v-btn
                outline
                small
                color="red"
                v-on:click="
                  deleteCertificate(certificate);
                  certificate = null;
                "
              >
                <v-icon small>close</v-icon> Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-alert
        :value="refreshCertificatesHint"
        type="warning"
        class="hint"
        transition="scale-transition"
      >
        Please, refresh certificates list
      </v-alert>
      <v-btn
        dark
        icon
        class="purple"
        v-on:click="
          fetchCertificates();
          refreshCertificatesHint = false;
        "
      >
        <v-icon>sync</v-icon>
      </v-btn>
      <v-btn
        dark
        icon
        class="purple"
        v-on:click="
          openCertificateRegistrationProcess = !openCertificateRegistrationProcess
        "
      >
        <v-icon>add</v-icon>
      </v-btn>
      <register-certificate
        v-if="openCertificateRegistrationProcess == true"
        v-on:abort="openCertificateRegistrationProcess = false"
        v-on:completed="
          openCertificateRegistrationProcess = false;
          refreshCertificatesHint = true;
        "
        :values="{
          devices: devices.map((d) => {
            return {text: d.device_id, ref: d.device_id};
          }),
        }"
      ></register-certificate>
    </v-card-actions>
  </v-card>
</template>
<script>
import {mapState} from "vuex";

export default {
  components: {
    itemlist: require("../../ItemList").default,
    "register-certificate": require("./RegisterCertificate").default,
  },

  props: {
    theme: String,
  },

  data() {
    return {
      refreshCertificatesHint: false,
      certificate: null,
      openCertificateRegistrationProcess: false,
      downloadingCertId: null,
    };
  },

  computed: {
    ...mapState({
      lastOperationMessage: "lastOperationMessage",
      lastOperationSuccess: "lastOperationSuccess",
      operationInProcess: "operationInProcess",
      operationInProcessMessage: "operationInProcessMessage",
      certificates: (state) =>
        (state.certificates && state.certificates.certificates) || [],
      devices: (state) => (state.devices && state.devices.devices) || [],
      appMetadata: (state) => state.metadata.models.views,
    }),
  },

  methods: {
    async fetchCertificates() {
      try {
        // Assuming a Vuex action exists: 'certificates/fetchCertificates'
        await this.$store.dispatch("certificates/fetchCertificates");
      } catch (error) {
        console.error("Error fetching certificates:", error);
      }
    },

    async downloadCertificate(certificateId) {
      try {
        this.downloadingCertId = certificateId;
        // Call API to download certificate
        const response = await this.$store.dispatch(
          "certificates/downloadCertificate",
          certificateId,
        );
        // Trigger browser download
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `certificate_${certificateId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } catch (error) {
        console.error("Error downloading certificate:", error);
      } finally {
        this.downloadingCertId = null;
      }
    },

    deleteCertificate(certificate) {
      if (
        confirm(
          `Are you sure you want to delete certificate "${certificate.certificate_id}"?`,
        )
      ) {
        try {
          this.$store.dispatch(
            "certificates/deleteCertificate",
            certificate.certificate_id,
          );
          this.refreshCertificatesHint = true;
        } catch (error) {
          console.error("Error deleting certificate:", error);
        }
      }
    },

    getCertTypeColor(type) {
      const colors = {
        "self-signed": "orange",
        csr: "blue",
        "aws-managed": "green",
      };
      return colors[type] || "grey";
    },

    getStatusColor(status) {
      const colors = {
        active: "green",
        inactive: "grey",
        revoked: "red",
      };
      return colors[status] || "blue";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date =
        typeof dateString === "object" ? dateString : new Date(dateString);
      return date.toLocaleString();
    },
  },

  mounted() {
    // Fetch certificates on component mount
    this.fetchCertificates();
  },
};
</script>

<style scoped>
.hint {
  margin-right: 1rem;
  padding: -0.5rem;
}
</style>
